import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import { BlockedActionMode } from '_lib/auth/rbac';
import { Ivim } from '_store/ivim/ivimsTypes';
import { ivimStatuses } from '_store/ivim/utils';
import { useLocalization } from '@fluent/react';
import { Card, CardContent, Typography } from '@material-ui/core';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import PictogramImage from "../../../../icons/ivim/PictogramImage";
import { images } from "../../../../icons/ivim/pictograms";
import { Accordion } from "../../../../items/Accordion";
import { CheckboxItem } from "../../../../items/CheckboxItem";
import { toLatLng } from '../../../../routes/MapView/utils';
import { DotComponent } from "../../../Event/DotComponent";
import { EventStatus } from "../../../Event/EventStatus";
import CardDetails from './Details';
import styles from './styles';

export interface CardListItemProps {
  text: string;
  ivim: Ivim;
  asCard: boolean;
  onUpdate?: (idExpanded: string) => void;
  onDelete?: (id: string) => void;
  onDisable?: (id: string) => void;
  onEnable?: (id: string) => void;
  onCheckedChange?: (id: string) => void;
  checked?: boolean;
  showCheckbox?: boolean;
  onClone?: (id: string) => void;
  connectedDenms?: boolean;
  showIvimPictogram?: boolean;
  writeAllowed?: boolean;
  blockedActionMode?: BlockedActionMode;
  blockedMessage?: string;
}

const CardListItem: FC<CardListItemProps> = ({
  text,
  ivim,
  onUpdate,
  onDelete,
  asCard,
  onDisable,
  onEnable,
  onCheckedChange,
  checked,
  showCheckbox = false,
  onClone,
  connectedDenms = false,
  showIvimPictogram = false,
  writeAllowed = true,
  blockedActionMode = 'disable',
  blockedMessage = '',
}) => {
  const { l10n } = useLocalization();
  const classes = styles();
  const dispatch = useAppDispatch();
  const mapRef = useSelector((state: RootState) => state.application.mapRef);

  const onAccordionExpanded = () => {
    const markerPosition = toLatLng(ivim?.referencePoint?.coordinates);
    mapRef.flyTo(markerPosition, 15, { animate: true, duration: 1 });

    setTimeout(() => {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.IVIM_PATH,
          id: ivim.id,
        }),
      );
    }, 1000);
  };

  const onAccordionClose = () => {
    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.IVIM_LIST,
        id: '2',
      }),
    );
  };

  const { isIvimActive, disabled, scheduled } = ivimStatuses(ivim);
  const ivimStartsDate = new Date(ivim.starts);

  const { statusClass, statusText } = EventStatus({
    active: isIvimActive,
    date: ivimStartsDate,
    disabled,
    scheduled,
  });

  const details = (
    <CardDetails
      ivim={ivim}
      onUpdate={() => (onUpdate ? onUpdate(ivim.id) : null)}
      onDelete={() => (onDelete ? onDelete(ivim.id) : null)}
      onClone={() => (onClone ? onClone(ivim.id) : null)}
      showButtons={true}
      isActive={isIvimActive}
      onDisable={() => (onDisable ? onDisable(ivim.id) : null)}
      onEnable={() => (onEnable ? onEnable(ivim.id) : null)}
      isDisabled={disabled}
      writeAllowed={writeAllowed}
      blockedActionMode={blockedActionMode}
      blockedMessage={blockedMessage}
    />
  );

  const body = (
    <div className={classes.cardOuterContent}>
      <div className={classes.checkbox}>
        {showCheckbox && (
          <CheckboxItem checked={checked} itemId={ivim.id} handleCheckedChange={onCheckedChange} />
        )}
      </div>
      <CardContent className={classes.cardContent}>
        <Typography className={classes.cardHeader}>
          {/* <img src={IvimIcon} className={classes.cardHeaderIcon} /> */}
          {connectedDenms && (
            <>
              <strong>Max Speed Limit Reduced to 110 due to HeavyRain</strong>
              <img src={images.speedLimit110} className={classes.cardHeaderIcon} />
            </>
          )}
        </Typography>
        <Typography className={classes.cardHeaderDescription}>
          <strong>{text}</strong>
          {showIvimPictogram && (
            <PictogramImage
              pictogramNature={ivim?.pictogramNature}
              pictogramSerialNumber={ivim?.pictogramSerialNumber}
              serviceSubCategoryCode={ivim?.serviceSubCategoryCode}
              style={{
                width: 'auto',
                height: '2rem',
                marginLeft: '0.5rem',
                verticalAlign: 'middle',
              }}
            />
          )}
        </Typography>
        <div className={classes.cardHeaderStatus}>
          {DotComponent({
            status: isIvimActive,
            isDisabled: disabled,
            scheduled,
          })}

          <Typography variant="caption" className={statusClass}>
            {statusText}
          </Typography>
        </div>
        {asCard ? (
          <div style={{ marginTop: '20px' }}>
            <Accordion
              title={l10n.getString('view-details')}
              onExpanded={onAccordionExpanded}
              onClose={onAccordionClose}
            >
              {details}
            </Accordion>
          </div>
        ) : (
          details
        )}
      </CardContent>
    </div>
  );

  if (asCard) {
    return <Card className={classes.root}>{body}</Card>;
  }
  return body;
};

export default React.memo(CardListItem);
